import React, { useMemo, useState } from 'react';
import { bcp47, useI18n } from '../i18n';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import {
  applyFilters,
  applySort,
  cellText,
  DataTableColumn,
  DataTableSort,
  DataTableSortDir,
  selectOptionsForColumn,
} from './model';

export type { DataTableAlign, DataTableColumn, DataTableFilter, DataTableSort, DataTableSortDir } from './model';

export interface DataTableProps<T> {
  rows: T[];
  columns: DataTableColumn<T>[];
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  dense?: boolean;
  /** Highlight the row whose id matches. */
  selectedId?: string | null;
  defaultSort?: DataTableSort;
  /** Hide the “n rows / clear filters” bar (sidebars, dialogs). */
  toolbar?: boolean;
  maxHeight?: number | string;
  selectedIds?: string[];
  onSelectedIdsChange?: (ids: string[]) => void;
}

function ColumnFilter<T>({
  col,
  value,
  options,
  onChange,
}: {
  col: DataTableColumn<T>;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  const { t } = useI18n();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const mode = col.filter ?? 'text';
  const open = Boolean(anchor);
  const active = Boolean(value);

  return (
    <>
      <IconButton
        size="small"
        color={active ? 'primary' : 'default'}
        aria-label={t('table.filterAria', { label: col.label })}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ p: 0.25 }}
      >
        <Badge color="primary" variant="dot" invisible={!active}>
          <FilterList fontSize="small" />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 1.5, width: 260 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {col.label}
          </Typography>
          {mode === 'select' ? (
            <List dense disablePadding>
              <ListItemButton selected={!value} onClick={() => onChange('')}>
                <ListItemText primary={t('table.all')} />
              </ListItemButton>
              {options.map((opt) => (
                <ListItemButton
                  key={opt.value}
                  selected={value === opt.value}
                  onClick={() => onChange(opt.value)}
                >
                  <ListItemText primary={opt.label} />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <TextField
              autoFocus
              size="small"
              fullWidth
              value={value}
              placeholder={t('table.contains')}
              onChange={(e) => onChange(e.target.value)}
              inputProps={{ 'aria-label': t('table.filterAria', { label: col.label }) }}
            />
          )}
          {active && (
            <Button size="small" fullWidth sx={{ mt: 1 }} onClick={() => onChange('')}>
              {t('table.clearFilters')}
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
}

/**
 * Canonical record table for the management UI.
 * Sortable + per-column filter on visible headers. Use this for every list of homogeneous rows.
 */
function DataTable<T>({
  rows,
  columns,
  getRowId,
  onRowClick,
  emptyMessage,
  dense = true,
  selectedId = null,
  defaultSort,
  toolbar = true,
  maxHeight,
  selectedIds,
  onSelectedIdsChange,
}: DataTableProps<T>) {
  const { t, locale } = useI18n();
  const empty = emptyMessage ?? t('table.empty');
  const localeTag = bcp47(locale);
  const [sortId, setSortId] = useState<string | null>(defaultSort?.id ?? null);
  const [sortDir, setSortDir] = useState<DataTableSortDir>(defaultSort?.dir ?? 'asc');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const selectable = Boolean(onSelectedIdsChange);
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);

  const sorted = useMemo(() => {
    const filtered = applyFilters(rows, columns, filters);
    const sort: DataTableSort | null = sortId ? { id: sortId, dir: sortDir } : null;
    return applySort(filtered, columns, sort, localeTag);
  }, [rows, columns, filters, sortId, sortDir, localeTag]);

  const filterActive = columns.some((col) => (filters[col.id] || '').trim());

  const handleSort = (id: string) => {
    if (sortId !== id) {
      setSortId(id);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortId(null);
    setSortDir('asc');
  };

  const setFilter = (id: string, value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });
  };

  const visibleIds = useMemo(() => sorted.map(getRowId), [sorted, getRowId]);
  const selectedVisibleCount = visibleIds.filter((id) => selectedSet.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const columnCount = columns.length + (selectable ? 1 : 0);

  const toggleRow = (id: string) => {
    if (!onSelectedIdsChange) {
      return;
    }
    const next = new Set(selectedSet);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onSelectedIdsChange(Array.from(next));
  };

  const toggleVisible = () => {
    if (!onSelectedIdsChange) {
      return;
    }
    const next = new Set(selectedSet);
    if (allVisibleSelected) {
      visibleIds.forEach((id) => next.delete(id));
    } else {
      visibleIds.forEach((id) => next.add(id));
    }
    onSelectedIdsChange(Array.from(next));
  };

  return (
    <Box>
      {toolbar && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} gap={1}>
          <Typography variant="caption" color="text.secondary">
            {sorted.length === rows.length
              ? t('table.rows', { count: rows.length })
              : t('table.rowsFiltered', { shown: sorted.length, total: rows.length })}
          </Typography>
          {filterActive && (
            <Button size="small" onClick={() => setFilters({})}>
              {t('table.clearFilters')}
            </Button>
          )}
        </Box>
      )}
      {!toolbar && filterActive && (
        <Box display="flex" justifyContent="flex-end" mb={0.5}>
          <Button size="small" onClick={() => setFilters({})}>
            {t('table.clearFilters')}
          </Button>
        </Box>
      )}
      <TableContainer
        component={Paper}
        elevation={1}
        sx={{
          maxHeight: maxHeight ?? 'none',
          overflow: 'auto',
        }}
      >
        <Table
          size={dense ? 'small' : 'medium'}
          stickyHeader={Boolean(maxHeight)}
          sx={{ minWidth: Math.max(480, columns.length * 120) }}
        >
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell
                  padding="checkbox"
                  sx={{ bgcolor: 'background.paper', width: 48 }}
                >
                  <Checkbox
                    size="small"
                    indeterminate={someVisibleSelected}
                    checked={allVisibleSelected}
                    onChange={toggleVisible}
                    inputProps={{ 'aria-label': t('table.selectAll') }}
                  />
                </TableCell>
              )}
              {columns.map((col) => {
                const sortable = col.sortable !== false;
                const filterable = (col.filter ?? 'text') !== 'none';
                const active = sortId === col.id;
                const optionRows = applyFilters(
                  rows,
                  columns,
                  Object.fromEntries(Object.entries(filters).filter(([key]) => key !== col.id)),
                );
                return (
                  <TableCell
                    key={col.id}
                    align={col.align}
                    sx={{
                      width: col.width,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      bgcolor: 'background.paper',
                    }}
                    sortDirection={active ? sortDir : false}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}
                      gap={0.25}
                    >
                      {sortable ? (
                        <TableSortLabel
                          active={active}
                          direction={active ? sortDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                      {filterable && (
                        <ColumnFilter
                          col={col}
                          value={filters[col.id] || ''}
                          options={selectOptionsForColumn(optionRows, col, localeTag)}
                          onChange={(value) => setFilter(col.id, value)}
                        />
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    {empty}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sorted.map((row) => {
                const id = getRowId(row);
                const selected = selectedId != null && selectedId === id;
                const checked = selectedSet.has(id);
                return (
                  <TableRow
                    key={id}
                    hover={Boolean(onRowClick)}
                    selected={selected || checked}
                    sx={onRowClick ? { cursor: 'pointer' } : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {selectable && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          size="small"
                          checked={checked}
                          onChange={() => toggleRow(id)}
                          inputProps={{ 'aria-label': t('table.selectRow') }}
                        />
                      </TableCell>
                    )}
                    {columns.map((col) => (
                      <TableCell
                        key={col.id}
                        align={col.align}
                        onClick={col.stopRowClick ? (e) => e.stopPropagation() : undefined}
                      >
                        {col.render ? col.render(row) : cellText(col.accessor(row)) || t('common.emdash')}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default DataTable;
